import { Theme } from '@emotion/react';
import { Box, CircularProgress, SxProps } from '@mui/material';
import AdvisorBackendService from 'AdvisorBackendService';
import BackendService from 'BackendService';
import React, { useEffect, useState } from 'react';
import FundOpportunity from '../models/FundOpportunity';
import Investor from '../models/Investor';
import PropertyCardGroup from './PropertyCardGroup';
import { Palette } from 'Palette';
import { StyledHeading } from './styles/styles';
import Pagination from './utils/Pagination';

export interface SharedPropertyListProps {
  investors: Investor[];
}

interface PropertyFilteredListProps {
  investors?: Investor[];
  title?: string;
  description?: string;
  /** Rendered inline, right-aligned against the main title — see PropertyCardGroup. */
  headerAction?: React.ReactNode;
  tone?: keyof typeof Palette;
  propertyCardTone?: keyof typeof Palette;
  noSpacing?: boolean;
  linkable?: boolean;
  routePrefix?: string;
  filterStatus: FundOpportunity['status'] | FundOpportunity['status'][];
  noTopPadding?: boolean;
  noTopMargin?: boolean;
  /** This list is the first/only thing in its page or tab panel — see PropertyCardGroup. */
  firstElement?: boolean;
  reduceTitleTopPadding?: boolean;
  subTitle?: string;
  subtitleVariable?: React.ComponentProps<typeof StyledHeading>['headerVariant'];
  inverseStatus?: boolean;
  overrideLinkTo?: 'details' | 'update';
  advisorMode?: boolean;
  sx?: SxProps<Theme>;
  ownerOnlyAllocations?: boolean;
  representativeOnlyAllocations?: boolean;
  dedupeFundOpportunities?: boolean;
  paginated?: boolean;
  pageSize?: number;
  paginationItemLabel?: string;
}

const PropertyFilteredList: React.FC<PropertyFilteredListProps> = ({
  investors,
  title,
  description,
  headerAction,
  tone,
  propertyCardTone,
  noSpacing,
  linkable,
  routePrefix,
  filterStatus,
  noTopPadding,
  noTopMargin,
  firstElement,
  reduceTitleTopPadding,
  subTitle,
  subtitleVariable,
  inverseStatus,
  overrideLinkTo,
  advisorMode = false,
  sx,
  ownerOnlyAllocations = false,
  representativeOnlyAllocations = false,
  dedupeFundOpportunities = false,
  paginated = false,
  pageSize = 10,
  paginationItemLabel = 'funds',
}) => {
  const [loading, setLoading] = useState(false);
  const [fetchedOpportunities, setFetchedOpportunities] = useState<FundOpportunity[] | null>(null);
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [investors]);

  useEffect(() => {
    if (!investors) {
      setLoading(true);
      // Use AdvisorBackendService if advisorMode is true
      const backendService = advisorMode ? new AdvisorBackendService() : new BackendService();
      // Type assertion needed since getFundOpportunityByStatus exists in admin BackendService
      // This code path is only executed when investors are not provided
      (backendService as any)
        .getFundOpportunityByStatus(filterStatus)
        .then((result: FundOpportunity[]) => setFetchedOpportunities(result))
        .finally(() => setLoading(false));
    }
  }, [investors, filterStatus, advisorMode]);

  const filterStatuses = [filterStatus].flat();
  const statusFilteredInvestors =
    investors?.filter(
      (investor: Investor) =>
        investor.fundOpportunity?.status &&
        (inverseStatus
          ? !filterStatuses.includes(investor.fundOpportunity.status)
          : filterStatuses.includes(investor.fundOpportunity.status)),
    ) || [];

  const allocationRole = representativeOnlyAllocations
    ? 'REPRESENTATIVE'
    : ownerOnlyAllocations
      ? 'OWNER'
      : null;

  const roleFilteredInvestors =
    allocationRole !== null
      ? statusFilteredInvestors.filter((investor: Investor) => {
          const entityAllocations = (investor as any).entityAllocations as
            | Array<{ contactRole?: string; allocatedAmount?: number | string }>
            | undefined;
          if (!entityAllocations || entityAllocations.length === 0) {
            return false;
          }

          return entityAllocations.some(
            (allocation) =>
              allocation?.contactRole === allocationRole &&
              Number(allocation?.allocatedAmount || 0) > 0,
          );
        })
      : statusFilteredInvestors;

  const filteredInvestors = dedupeFundOpportunities
    ? (() => {
        const byFundId = new Map<string, Investor>();

        roleFilteredInvestors.forEach((investor: Investor) => {
          const fundId = investor.fundOpportunity?.id;
          if (!fundId) {
            return;
          }

          const allocatedAmount =
            allocationRole !== null
              ? (
                  ((investor as any).entityAllocations as
                    | Array<{
                        contactRole?: string;
                        allocatedAmount?: number | string;
                      }>
                    | undefined) || []
                )
                  .filter((allocation) => allocation?.contactRole === allocationRole)
                  .reduce((sum, allocation) => sum + Number(allocation?.allocatedAmount || 0), 0)
              : Number(investor.committedInvestment || 0);

          const existing = byFundId.get(fundId);
          if (existing) {
            existing.committedInvestment =
              Number(existing.committedInvestment || 0) + allocatedAmount;
          } else {
            byFundId.set(fundId, {
              ...investor,
              committedInvestment: allocatedAmount,
            });
          }
        });

        return Array.from(byFundId.values()).filter(
          (investor) => Number(investor.committedInvestment || 0) > 0,
        );
      })()
    : roleFilteredInvestors;

  if (loading) {
    return <CircularProgress />;
  }

  if (fetchedOpportunities?.length === 0) {
    return null;
  }

  if (investors && filteredInvestors.length === 0) {
    return null;
  }

  const totalPages = paginated ? Math.max(1, Math.ceil(filteredInvestors.length / pageSize)) : 1;
  const pagedInvestors = paginated
    ? filteredInvestors.slice((page - 1) * pageSize, page * pageSize)
    : filteredInvestors;

  return (
    <Box>
      <PropertyCardGroup
        investors={pagedInvestors}
        fundOpportunities={fetchedOpportunities || undefined}
        sourceType={investors ? 'investor' : 'fundOpportunity'}
        title={title}
        subTitle={subTitle}
        subtitleVariable={subtitleVariable}
        description={description}
        headerAction={headerAction}
        tone={tone}
        propertyCardTone={propertyCardTone}
        noSpacing={noSpacing}
        linkable={linkable}
        linkTo={overrideLinkTo}
        routePrefix={routePrefix}
        contentAware={true}
        preventFullSizePopup={true}
        noTopPadding={noTopPadding}
        noTopMargin={noTopMargin}
        firstElement={firstElement}
        reduceTitleTopPadding={reduceTitleTopPadding}
        sx={sx}
      />
      {paginated && totalPages > 1 && (
        <Box sx={{ mt: 2 }}>
          <Pagination
            pagination={{
              page,
              limit: pageSize,
              total: filteredInvestors.length,
              totalPages,
            }}
            onPageChange={setPage}
            itemLabel={paginationItemLabel}
          />
        </Box>
      )}
    </Box>
  );
};

export default PropertyFilteredList;
